import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { parseBulkBooking, saveBulkBookings, getStaff, createAgency, parseBulkExcel } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Lightning, Warning, CheckCircle, Trash, CalendarBlank, MagnifyingGlass, Plus, CaretDown, WhatsappLogo, FileXls, UploadSimple } from '@phosphor-icons/react';
import { SearchableSelect } from '../components/ui/searchable-select';
import { AgencyMatchDialog } from '../components/bulk-booking/AgencyMatchDialog';
import { NewAgencyDialog } from '../components/bulk-booking/NewAgencyDialog';

const STEPS = { INPUT: 'input', PREVIEW: 'preview', SUCCESS: 'success' };
const INPUT_MODES = { PASTE: 'paste', EXCEL: 'excel' };

export default function BulkBookingPage({ embedded = false }) {
  const { user } = useAuth();
  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const isAdmin = isOwner || user?.role === 'assistant';

  const [step, setStep] = useState(STEPS.INPUT);
  const [inputMode, setInputMode] = useState(INPUT_MODES.PASTE);
  const [text, setText] = useState('');
  const [excelFile, setExcelFile] = useState(null);
  const [source, setSource] = useState('tataneu');
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [entries, setEntries] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedResult, setSavedResult] = useState(null);
  const [parsedMeta, setParsedMeta] = useState(null);
  const [entryFiles, setEntryFiles] = useState({});  // { idx: File }

  // Date override
  const [useManualDate, setUseManualDate] = useState(false);
  const [manualDate, setManualDate] = useState('');
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

  // New Agency Dialog
  const [newAgencyDialog, setNewAgencyDialog] = useState(false);
  const [newAgencyData, setNewAgencyData] = useState({ name: '', company_type: '', rate: '', location: '' });
  const [pendingEntryIdx, setPendingEntryIdx] = useState(null);

  // Match Selection Dialog
  const [matchDialog, setMatchDialog] = useState(false);
  const [matchEntryIdx, setMatchEntryIdx] = useState(null);
  const [matchOptions, setMatchOptions] = useState([]);

  useEffect(() => {
    if (isAdmin) {
      getStaff().then(res => setStaffList(res.data.filter(s => s.role === 'staff'))).catch(() => {});
    }
  }, [isAdmin]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount || 0);
  const round = (n) => Math.round(n * 100) / 100;

  const handleParse = async () => {
    if (inputMode === INPUT_MODES.PASTE && !text.trim()) { toast.error('Booking data paste karo'); return; }
    if (inputMode === INPUT_MODES.EXCEL && !excelFile) { toast.error('Excel/CSV file select karo'); return; }

    setLoading(true);
    try {
      let res;
      if (inputMode === INPUT_MODES.EXCEL) {
        const fd = new FormData();
        fd.append('file', excelFile);
        fd.append('source', source);
        fd.append('staff_id', selectedStaffId || '');
        fd.append('booking_date', useManualDate ? manualDate : '');
        res = await parseBulkExcel(fd);
      } else {
        const payload = {
          text,
          source,
          staff_id: selectedStaffId || undefined,
          booking_date: useManualDate ? manualDate : undefined
        };
        res = await parseBulkBooking(payload);
      }
      setEntries(res.data.entries || []);
      setErrors(res.data.errors || []);
      setParsedMeta(res.data);
      setStep(STEPS.PREVIEW);

      if (res.data.date_source === 'auto_detected') {
        toast.success(`Date auto-detected: ${res.data.booking_date}`);
      }
      if (res.data.is_backdate) {
        toast.warning(`BACKDATE Entry! Date: ${res.data.booking_date}`);
      }
      if (res.data.duplicate_count > 0) {
        toast.warning(`${res.data.duplicate_count} duplicate entries detected`);
      }
      if (res.data.needs_selection > 0) {
        toast.info(`${res.data.needs_selection} agencies ko manually select karna hoga`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Parse failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAgency = (entryIdx, match) => {
    const updated = [...entries];
    const entry = updated[entryIdx];
    const feePercent = parsedMeta?.fee_percent || 0;
    const amount = round(match.rate * entry.quantity);
    const fee = round(amount * feePercent / 100);
    const total = amount + fee;
    updated[entryIdx] = {
      ...entry, agency_name: match.name, agency_id: match.id, rate: match.rate,
      amount, convenience_fee: fee, total_amount: total,
      is_new_agency: false, agency_status: match.status || 'active', match_score: 100, fuzzy_matches: [],
    };
    setEntries(updated);
    setMatchDialog(false);
  };

  const openMatchDialog = (idx) => {
    setMatchEntryIdx(idx);
    setMatchOptions(entries[idx].fuzzy_matches || []);
    setMatchDialog(true);
  };

  const openNewAgencyDialog = (idx) => {
    setPendingEntryIdx(idx);
    setNewAgencyData({
      name: entries[idx].original_text || entries[idx].agency_name || '',
      company_type: entries[idx].agency_type || '', rate: '', location: ''
    });
    setNewAgencyDialog(true);
  };

  const handleCreateNewAgency = async () => {
    if (!newAgencyData.name.trim()) { toast.error('Agency name required'); return; }
    try {
      const res = await createAgency({ name: newAgencyData.name.trim().toUpperCase(), rate: parseFloat(newAgencyData.rate) || 0 });
      const newAgency = res.data;
      toast.success(`Agency "${newAgency.name}" created (Pending Approval)`);
      if (pendingEntryIdx !== null) {
        const updated = [...entries];
        const entry = updated[pendingEntryIdx];
        const rate = newAgency.rate || 0;
        const feePercent = parsedMeta?.fee_percent || 0;
        const amount = round(rate * entry.quantity);
        const fee = round(amount * feePercent / 100);
        const total = amount + fee;
        updated[pendingEntryIdx] = {
          ...entry, agency_name: newAgency.name, agency_id: newAgency.id, rate,
          amount: rate > 0 ? amount : null, convenience_fee: rate > 0 ? fee : null,
          total_amount: rate > 0 ? total : null, is_new_agency: true, agency_status: 'pending_approval',
        };
        setEntries(updated);
      }
      setNewAgencyDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Agency create failed');
    }
  };

  const removeEntry = (idx) => setEntries(entries.filter((_, i) => i !== idx));

  const handleSave = async () => {
    const unresolved = entries.filter(e => !e.agency_id);
    if (unresolved.length > 0) {
      toast.error(`${unresolved.length} entries mein agency select nahi hai`);
      return;
    }
    const valid = entries.filter(e => e.agency_id && e.rate > 0);
    if (valid.length === 0) { toast.error('Koi valid entry nahi hai'); return; }

    // Check mandatory files
    const missing = valid.filter((e) => {
      const realIdx = entries.indexOf(e);
      return !entryFiles[realIdx];
    });
    if (missing.length > 0) {
      toast.error(`${missing.length} agency ke liye report file upload karo (mandatory)`);
      return;
    }

    setSaving(true);
    try {
      const res = await saveBulkBookings({
        entries: entries.map(e => ({
          agency_name: e.agency_name, agency_id: e.agency_id, quantity: e.quantity,
          rate: e.rate, is_new_agency: e.is_new_agency, is_duplicate_confirmed: e.is_duplicate || false,
        })),
        source,
        staff_id: selectedStaffId || undefined,
        booking_date: parsedMeta?.booking_date || undefined
      });

      // Upload per-agency files as bulk entry reports
      const { uploadBulkEntryReport } = await import('../lib/api');
      for (const [idxStr, file] of Object.entries(entryFiles)) {
        const idx = parseInt(idxStr);
        const entry = entries[idx];
        if (entry && entry.agency_id && file) {
          try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('agency_id', entry.agency_id);
            fd.append('agency_name', entry.agency_name);
            fd.append('description', `Bulk Entry Report: ${entry.agency_name} x${entry.quantity} — ${parsedMeta?.booking_date || 'Today'}`);
            await uploadBulkEntryReport(fd);
          } catch (err) { console.error('Report upload failed:', entry.agency_name, err); }
        }
      }

      setSavedResult(res.data);
      setStep(STEPS.SUCCESS);
      toast.success(`${res.data.total_bookings} bookings saved with reports!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const resetAll = () => {
    setStep(STEPS.INPUT); setText(''); setSource('tataneu'); setSelectedStaffId('');
    setEntries([]); setErrors([]); setSavedResult(null); setParsedMeta(null);
    setUseManualDate(false); setManualDate(''); setEntryFiles({});
  };

  // Edit quantity in preview
  const handleQtyChange = (idx, newQty) => {
    const q = parseInt(newQty) || 0;
    if (q < 1) return;
    const updated = [...entries];
    const e = { ...updated[idx] };
    e.quantity = q;
    if (e.rate) {
      e.amount = Math.round(e.rate * q * 100) / 100;
      e.convenience_fee = e.convenience_fee ? Math.round((e.convenience_fee / (updated[idx].quantity || 1)) * q * 100) / 100 : 0;
      e.total_amount = Math.round((e.amount + (e.convenience_fee || 0)) * 100) / 100;
    }
    updated[idx] = e;
    setEntries(updated);
  };

  // Per-agency file upload
  const handleEntryFile = (idx, file) => {
    setEntryFiles(prev => ({ ...prev, [idx]: file }));
  };

  // Download agency report as Excel
  const downloadAgencyExcel = (entry) => {
    import('xlsx').then(XLSX => {
      const date = parsedMeta?.booking_date || new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
      const ws = XLSX.utils.aoa_to_sheet([
        ['Agency Name', 'Quantity', 'Rate', 'Amount', 'Date', 'Source'],
        [entry.agency_name, entry.quantity, entry.rate || 0, entry.total_amount || 0, date, source.toUpperCase()]
      ]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Report');
      XLSX.writeFile(wb, `${entry.agency_name}_${date}.xlsx`);
    }).catch(() => {
      // Fallback: create CSV
      const date = parsedMeta?.booking_date || new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
      const csv = `Agency Name,Quantity,Rate,Amount,Date,Source\n${entry.agency_name},${entry.quantity},${entry.rate || 0},${entry.total_amount || 0},${date},${source.toUpperCase()}`;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${entry.agency_name}_${date}.csv`; a.click();
      URL.revokeObjectURL(url);
    });
  };

  // Check all ready entries have files
  const readyEntries = entries.filter(e => e.agency_id && e.rate > 0);
  const allFilesUploaded = readyEntries.every((_, idx) => {
    const realIdx = entries.indexOf(readyEntries[idx] || entries[idx]);
    // Find the actual index in entries array
    return true; // will recalculate below
  });
  const missingFiles = readyEntries.filter((e, i) => {
    const realIdx = entries.findIndex(en => en === e);
    return !entryFiles[realIdx];
  });
  const canSave = readyEntries.length > 0 && missingFiles.length === 0 && entries.filter(e => !e.agency_id).length === 0;

  const getStatusBadge = (entry) => {
    if (entry.agency_status === 'needs_selection') return <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Select Agency</span>;
    if (entry.is_new_agency && !entry.agency_id) return <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">New Agency</span>;
    if (entry.agency_status === 'pending_approval') return <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Pending</span>;
    if (entry.match_score && entry.match_score < 100) return <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Fuzzy ({entry.match_score}%)</span>;
    return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Matched</span>;
  };

  // ======================== SUCCESS ========================
  if (step === STEPS.SUCCESS) {
    return (
      <div className="space-y-6" data-testid="bulk-booking-success">
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <CheckCircle size={48} className="text-green-600 mx-auto mb-4" weight="fill" />
          <h2 className="text-2xl font-bold text-green-800 mb-2">Bookings Saved!</h2>
          <p className="text-green-700">{savedResult?.total_bookings} bookings | Total: {formatCurrency(savedResult?.total_amount)}</p>
          {savedResult?.new_agencies?.length > 0 && (
            <p className="text-orange-600 mt-2">{savedResult.new_agencies.length} new agencies (Pending Approval)</p>
          )}
          <Button onClick={resetAll} className="mt-6 btn-primary" data-testid="bulk-new-batch-btn">New Batch</Button>
        </div>
      </div>
    );
  }

  // ======================== MAIN RENDER ========================
  return (
    <div className="space-y-6" data-testid="bulk-booking-page">
      <div className="flex items-center justify-between">
        <h1 className={`${embedded ? 'text-lg' : 'text-2xl'} font-bold text-gray-900 flex items-center gap-2`}>
          <Lightning size={embedded ? 22 : 28} weight="fill" className="text-[#002FA7]" /> Bulk Entry
        </h1>
        {step === STEPS.PREVIEW && (
          <Button variant="outline" onClick={() => setStep(STEPS.INPUT)} data-testid="bulk-back-btn">Back to Edit</Button>
        )}
      </div>

      {/* STEP 1: INPUT */}
      {step === STEPS.INPUT && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
          {/* Input Mode Toggle */}
          <div className="flex gap-1 border-b border-gray-200 -mt-2 mb-2">
            <button onClick={() => setInputMode(INPUT_MODES.PASTE)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${inputMode === INPUT_MODES.PASTE ? 'border-green-600 text-green-700' : 'border-transparent text-gray-400 hover:text-gray-600'}`} data-testid="mode-paste">
              <WhatsappLogo size={18} weight="fill" /> WhatsApp Paste
            </button>
            <button onClick={() => setInputMode(INPUT_MODES.EXCEL)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${inputMode === INPUT_MODES.EXCEL ? 'border-green-600 text-green-700' : 'border-transparent text-gray-400 hover:text-gray-600'}`} data-testid="mode-excel">
              <FileXls size={18} /> Excel/CSV Upload
            </button>
          </div>

          {/* Source + Staff */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="form-label">Payment Source</Label>
              <SearchableSelect
                options={[
                  { value: 'tataneu', label: 'TataNeu', sublabel: '2.067% fee' },
                  { value: 'amazon', label: 'Amazon', sublabel: '0% fee' }
                ]}
                value={source}
                onValueChange={setSource}
                placeholder="Source select karo"
                data-testid="bulk-source-select"
              />
            </div>
            {isAdmin && staffList.length > 0 && (
              <div>
                <Label className="form-label">Staff</Label>
                <SearchableSelect
                  options={staffList.map(s => ({ value: s.id, label: s.name, sublabel: s.email }))}
                  value={selectedStaffId}
                  onValueChange={setSelectedStaffId}
                  placeholder="Staff select karo"
                  data-testid="bulk-staff-select"
                />
              </div>
            )}
          </div>

          {/* Date Override (optional) */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <CalendarBlank size={20} className="text-gray-600" />
              <Label className="font-semibold text-gray-700">Date</Label>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={!useManualDate} onChange={() => { setUseManualDate(false); setManualDate(''); }} className="accent-[#002FA7]" />
                <span className="text-sm">{inputMode === INPUT_MODES.PASTE ? 'Auto-detect from text (ya today)' : 'Today'}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={useManualDate} onChange={() => setUseManualDate(true)} className="accent-[#002FA7]" />
                <span className="text-sm">Manual Override</span>
              </label>
              {useManualDate && (
                <Input type="date" value={manualDate} max={todayStr} onChange={(e) => setManualDate(e.target.value)} className="w-44" data-testid="bulk-date-picker" />
              )}
            </div>
          </div>

          {/* WhatsApp Paste Input */}
          {inputMode === INPUT_MODES.PASTE && (
            <div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3 mb-3">
                <WhatsappLogo size={24} className="text-green-600" weight="fill" />
                <div>
                  <p className="text-sm font-medium text-green-800">WhatsApp se direct paste karo!</p>
                  <p className="text-xs text-green-600">Date auto-detect hogi. Sirf agency name aur quantity chahiye.</p>
                </div>
              </div>
              <Label className="form-label">Booking Data Paste Karo</Label>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={`WhatsApp se copy-paste karo:\n\n05th April 2026\n\nAjay Bharat Gas =48\nChandra Indane bihar =14\nJATAN BHARATGAS=182\nRANJIT BHARAT GAS=22\nKISHORI INDANE =19`}
                className="min-h-[220px] font-mono text-sm"
                data-testid="bulk-text-input"
              />
              <p className="text-xs text-gray-500 mt-1">Date auto-detect hogi | Format: Agency Name =Quantity | Extra words (bihar, gas service) auto-ignore</p>
            </div>
          )}

          {/* Excel/CSV Upload Input */}
          {inputMode === INPUT_MODES.EXCEL && (
            <div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3 mb-3">
                <FileXls size={24} className="text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Excel/CSV file upload karo!</p>
                  <p className="text-xs text-blue-600">Column A: Agency Name, Column B: Quantity. First row header hona chahiye.</p>
                </div>
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer" onClick={() => document.getElementById('bulk-excel-input').click()}>
                <input
                  id="bulk-excel-input"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setExcelFile(e.target.files[0])}
                  className="hidden"
                  data-testid="bulk-excel-input"
                />
                {excelFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileXls size={28} className="text-green-600" />
                    <div>
                      <p className="text-sm font-semibold text-green-700">{excelFile.name}</p>
                      <p className="text-xs text-gray-500">{(excelFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setExcelFile(null); }} className="ml-3 text-red-500 hover:text-red-700"><Trash size={18} /></button>
                  </div>
                ) : (
                  <div>
                    <UploadSimple size={32} className="text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 font-medium">Click to select Excel/CSV file</p>
                    <p className="text-xs text-gray-400 mt-1">.xlsx, .xls, .csv supported</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <Button onClick={handleParse} disabled={loading || (inputMode === INPUT_MODES.PASTE && !text.trim()) || (inputMode === INPUT_MODES.EXCEL && !excelFile)} className="btn-primary w-full" data-testid="bulk-parse-btn">
            {loading ? 'Parsing...' : <><MagnifyingGlass size={18} className="mr-2" /> Parse & Match Agencies</>}
          </Button>
        </div>
      )}

      {/* STEP 2: PREVIEW */}
      {step === STEPS.PREVIEW && (
        <div className="space-y-4">
          {/* Summary Bar */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex flex-wrap gap-4 items-center justify-between mb-2">
              <div className="flex gap-5 text-sm">
                <span><strong>{entries.length}</strong> Entries</span>
                <span className="text-green-600"><strong>{entries.filter(e => e.agency_id && e.rate > 0).length}</strong> Ready</span>
                {entries.filter(e => e.agency_status === 'needs_selection').length > 0 && (
                  <span className="text-amber-600"><strong>{entries.filter(e => e.agency_status === 'needs_selection').length}</strong> Select Needed</span>
                )}
                {entries.filter(e => e.is_new_agency && !e.agency_id).length > 0 && (
                  <span className="text-purple-600"><strong>{entries.filter(e => e.is_new_agency && !e.agency_id).length}</strong> New</span>
                )}
                {parsedMeta?.duplicate_count > 0 && <span className="text-red-600"><strong>{parsedMeta.duplicate_count}</strong> Duplicates</span>}
              </div>
              <div className="text-lg font-bold">{formatCurrency(entries.reduce((s, e) => s + (e.total_amount || 0), 0))}</div>
            </div>

            {/* Date info */}
            <div className="flex items-center gap-2 text-sm">
              <CalendarBlank size={16} className="text-gray-500" />
              <span className="font-medium">Date: {parsedMeta?.booking_date}</span>
              {parsedMeta?.date_source === 'auto_detected' && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-Detected</span>
              )}
              {parsedMeta?.date_source === 'manual' && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Manual</span>
              )}
              {parsedMeta?.date_source === 'today' && (
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">Today</span>
              )}
            </div>
          </div>

          {parsedMeta?.is_backdate && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-amber-700">
              <Warning size={20} weight="fill" /> BACKDATE Entry: {parsedMeta.booking_date}
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-700 mb-2">Parse Errors ({errors.length})</h3>
              {errors.map((e, i) => <p key={`err-${e.line_number}-${i}`} className="text-sm text-red-600">Line {e.line_number}: {e.error}</p>)}
            </div>
          )}

          {/* Entries Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-8">#</TableHead>
                  <TableHead>Agency</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right w-24">Qty</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="w-48">Report File</TableHead>
                  <TableHead className="text-right w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry, idx) => (
                  <TableRow
                    key={`entry-${entry.line_number}-${entry.agency_name}`}
                    className={entry.is_duplicate ? 'bg-red-50' : entry.agency_status === 'needs_selection' ? 'bg-amber-50' : entry.is_new_agency && !entry.agency_id ? 'bg-purple-50' : ''}
                    data-testid={`bulk-entry-row-${idx}`}
                  >
                    <TableCell className="text-gray-500 text-xs">{entry.line_number}</TableCell>
                    <TableCell>
                      <span className="font-medium">{entry.agency_name}</span>
                      {entry.original_text && entry.original_text !== entry.agency_name && (
                        <span className="text-xs text-gray-400 block">{entry.original_text}</span>
                      )}
                      {entry.is_duplicate && (
                        <span className="text-xs text-red-600 flex items-center gap-1 mt-0.5">
                          <Warning size={12} weight="fill" /> {entry.duplicate_info}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(entry)}</TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number" min="1"
                        value={entry.quantity}
                        onChange={(e) => handleQtyChange(idx, e.target.value)}
                        className="w-20 text-right font-mono text-sm h-8 ml-auto"
                        data-testid={`bulk-qty-${idx}`}
                      />
                    </TableCell>
                    <TableCell className="text-right font-mono text-emerald-700 font-semibold">{entry.rate ? `💰 ${formatCurrency(entry.rate)}` : '-'}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{entry.total_amount ? formatCurrency(entry.total_amount) : '-'}</TableCell>
                    <TableCell>
                      {entry.agency_id && (
                        <div className="space-y-1">
                          {entryFiles[idx] ? (
                            <div className="flex items-center gap-1 bg-green-50 border border-green-200 rounded px-2 py-1">
                              <CheckCircle size={14} className="text-green-600" weight="fill" />
                              <span className="text-[10px] text-green-700 truncate max-w-[100px]">{entryFiles[idx].name}</span>
                              <button onClick={() => { const f = { ...entryFiles }; delete f[idx]; setEntryFiles(f); }} className="text-red-400 hover:text-red-600 ml-auto"><Trash size={12} /></button>
                            </div>
                          ) : (
                            <div className="relative">
                              <input type="file" accept=".xlsx,.xls,.csv,.txt,.pdf,.jpg,.jpeg,.png" onChange={(e) => { if (e.target.files[0]) handleEntryFile(idx, e.target.files[0]); }} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid={`bulk-file-${idx}`} />
                              <div className="flex items-center gap-1 border border-dashed border-red-300 rounded px-2 py-1 text-red-500 text-[10px] font-medium hover:border-red-500 transition-colors">
                                <UploadSimple size={12} /> Upload Report *
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        {entry.agency_id && (
                          <Button size="sm" variant="ghost" className="text-green-600 h-7 w-7 p-0" onClick={() => downloadAgencyExcel(entry)} title="Download Report" data-testid={`bulk-download-${idx}`}>
                            <FileXls size={14} />
                          </Button>
                        )}
                        {entry.agency_status === 'needs_selection' && (
                          <Button size="sm" variant="outline" className="text-amber-600 text-xs h-7" onClick={() => openMatchDialog(idx)} data-testid={`bulk-select-agency-${idx}`}>
                            <CaretDown size={14} className="mr-1" /> Select
                          </Button>
                        )}
                        {entry.is_new_agency && !entry.agency_id && (
                          <Button size="sm" variant="outline" className="text-purple-600 text-xs h-7" onClick={() => openNewAgencyDialog(idx)} data-testid={`bulk-add-agency-${idx}`}>
                            <Plus size={14} className="mr-1" /> Add
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0" onClick={() => removeEntry(idx)} data-testid={`bulk-remove-${idx}`}>
                          <Trash size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Missing files warning */}
          {missingFiles.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
              <Warning size={18} weight="fill" /> {missingFiles.length} agency ke liye report file upload karna mandatory hai!
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={saving || !canSave}
            className="btn-primary w-full text-lg py-6"
            data-testid="bulk-save-btn"
          >
            {saving ? 'Saving...' : <>
              <CheckCircle size={22} className="mr-2" weight="fill" />
              Save {readyEntries.length} Bookings | {formatCurrency(entries.reduce((s, e) => s + (e.total_amount || 0), 0))}
            </>}
          </Button>
        </div>
      )}

      {/* Agency Match Selection Dialog */}
      <AgencyMatchDialog
        matchDialog={matchDialog}
        setMatchDialog={setMatchDialog}
        matchEntryIdx={matchEntryIdx}
        entries={entries}
        matchOptions={matchOptions}
        handleSelectAgency={handleSelectAgency}
        openNewAgencyDialog={openNewAgencyDialog}
        formatCurrency={formatCurrency}
      />

      {/* New Agency Dialog */}
      <NewAgencyDialog
        newAgencyDialog={newAgencyDialog}
        setNewAgencyDialog={setNewAgencyDialog}
        newAgencyData={newAgencyData}
        setNewAgencyData={setNewAgencyData}
        handleCreateNewAgency={handleCreateNewAgency}
      />
    </div>
  );
}
